import React, { useState, useContext, useEffect } from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import MUIDataTable from 'mui-datatables';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { mutate as GlobalMutate } from 'swr';
import { useFetch } from '../../components/hooks/useFetch';
import { columns } from '../../components/employers_clients/equipmentView/columns';
import colourStyles from '../../components/selectAsync/colourStyles';
import { Context } from '../../services/context';
import api from '../../services/api';
import { Prefix } from '../../services/prefix';
import { getTableOptions, handleDelete } from './utils';
import GlobalSelect from './selectlist';

export const EquipmentView = ({ idUser }) => {
  const { employeeView, setEmployeeView } = useContext(Context);

  const [pagina, setPagina] = useState(0);
  const [perPage, setPerPage] = useState(215);
  const [search, setSearch] = useState('');
  const [uf, setUf] = useState(null);
  const [city, setCity] = useState(null);
  const [unit, setUnit] = useState(null);
  const [sector, setSector] = useState(null);
  const [room, setRoom] = useState(null);
  const [ufOptions, setUfOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  const [sectorOptions, setSectorOptions] = useState([]);
  const [roomOptions, setRoomOptions] = useState([]);
  const [loadingUFs, setLoadingUFs] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [loadingSectors, setLoadingSectors] = useState(false);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [contractItemsOptions, setContractItemsOptions] = useState([]);
  const [loadingContractItems, setLoadingContractItems] = useState(false);
  const [selectedContractItem, setSelectedContractItem] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [valueSelect, setValueSelect] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchUFs = async () => {
    setLoadingUFs(true);
    try {
      const res = await api.get(`${Prefix.replace(/[\\"]/g, '')}/states`);
      const data = res.data?.models?.data?.map(uf => ({ value: uf.uf, label: uf.uf })) || [];
      setUfOptions(data);
      return { options: data, hasMore: false };
    } finally {
      setLoadingUFs(false);
    }
  };

  const fetchCities = async (inputValue = '', page = 1) => {
    if (!uf?.value) return { options: [], hasMore: false };
    setLoadingCities(true);
    try {
      const res = await api.get(`${Prefix.replace(/[\\"]/g, '')}/cities`, {
        params: {
          per_page: 225,
          page,
          search: inputValue,
          uf: uf.value
        }
      });
      const data = res.data?.models?.data?.map(c => ({ value: c.id, label: c.name })) || [];
      const lastPage = res.data?.models?.last_page || 1;
      const hasMore = page < lastPage;
      if (page === 1) setCityOptions(data);
      else setCityOptions(prev => [...prev, ...data]);
      return { options: data, hasMore };
    } finally {
      setLoadingCities(false);
    }
  };

  const fetchUnits = async (inputValue = '', page = 1) => {
    if (!uf?.value || !city?.value) return { options: [], hasMore: false };
    setLoadingUnits(true);
    try {
      const res = await api.get(`${Prefix.replace(/[\\"]/g, '')}/units`, {
        params: {
          all: false,
          per_page: 200,
          page,
          search: inputValue,
          uf: uf.value,
          id_city: city.value
        }
      });
      const data = res.data?.models?.data?.map(u => ({ value: u.id, label: u.name })) || [];
      const lastPage = res.data?.models?.last_page || 1;
      const hasMore = page < lastPage;
      if (page === 1) setUnitOptions(data);
      else setUnitOptions(prev => [...prev, ...data]);
      return { options: data, hasMore };
    } finally {
      setLoadingUnits(false);
    }
  };

  const fetchSectors = async () => {
    if (!unit?.value) return { options: [], hasMore: false };
    setLoadingSectors(true);
    try {
      const res = await api.get(`${Prefix.replace(/[\\"]/g, '')}/sectors`, {
        params: { id_unit: unit.value }
      });
      const data = res.data?.models?.data?.map(s => ({ value: s.id, label: s.name })) || [];
      setSectorOptions(data);
      return { options: data, hasMore: false };
    } finally {
      setLoadingSectors(false);
    }
  };

  const fetchRooms = async (inputValue = '', page = 1) => {
    if (!unit?.value || !sector?.value) return { options: [], hasMore: false };
    setLoadingRooms(true);
    try {
      const res = await api.get(`${Prefix.replace(/[\\"]/g, '')}/rooms`, {
        params: {
          all: '',
          per_page: 200,
          page,
          search: inputValue,
          id_sector: sector.value,
          id_unit: unit.value
        }
      });
      const data = res.data?.models?.data?.map(r => ({ value: r.id, label: r.name })) || [];
      const lastPage = res.data?.models?.last_page || 1;
      const hasMore = page < lastPage;
      if (page === 1) setRoomOptions(data);
      else setRoomOptions(prev => [...prev, ...data]);
      return { options: data, hasMore };
    } finally {
      setLoadingRooms(false);
    }
  };

  const fetchContractItems = async (inputValue = '', page = 1) => {
    if (!unit?.value) return { options: [], hasMore: false };
    setLoadingContractItems(true);
    try {
      const res = await api.get(`${Prefix.replace(/[\\"]/g, '')}/contract-items`, {
        params: {
          all: '',
          per_page: 200,
          page,
          uf: uf?.value || '',
          id_city: city?.value || '',
          id_unit: unit?.value || '',
          id_sector: sector?.value || '',
          id_room: room?.value || '',
          search: inputValue || ''
        }
      });
      const data = res.data?.models?.data?.map(ci => ({
        value: ci.id,
        label: `${ci.nickname} - ${ci.serial}`,
        raw: ci
      })) || [];
      const lastPage = res.data?.models?.last_page || 1;
      const hasMore = page < lastPage;
      if (page === 1) setContractItemsOptions(data);
      else setContractItemsOptions(prev => [...prev, ...data]);
      return { options: data, hasMore };
    } finally {
      setLoadingContractItems(false);
    }
  };

  const fetchAlerts = async () => {
    try {
      const res = await api.get(`${Prefix.replace(/[\\"]/g, '')}/alerts`);
      const data = res.data?.models?.data?.map(a => ({ value: a.value, name: a.name })) || [];
      if (!data.length) {
        setAlerts([
          { value: true, name: 'Sim' },
          { value: false, name: 'Não' }
        ]);
      } else {
        setAlerts(
          data.map(a => ({
            value: a.value === true || a.value === 'true',
            name: a.name
          }))
        );
      }
    } catch {
      setAlerts([
        { value: true, name: 'Sim' },
        { value: false, name: 'Não' }
      ]);
    }
  };

  useEffect(() => {
    fetchUFs();
    fetchAlerts();
  }, []);

  useEffect(() => {
    setCity(null);
    setUnit(null);
    setSector(null);
    setRoom(null);
    setCityOptions([]);
    setUnitOptions([]);
    setSectorOptions([]);
    setRoomOptions([]);
    setContractItemsOptions([]);
    setSelectedContractItem(null);
    if (uf?.value) fetchCities('');
  }, [uf]);

  useEffect(() => {
    setUnit(null);
    setSector(null);
    setRoom(null);
    setUnitOptions([]);
    setSectorOptions([]);
    setRoomOptions([]);
    setContractItemsOptions([]);
    setSelectedContractItem(null);
    if (city?.value) fetchUnits('');
  }, [city]);

  useEffect(() => {
    setSector(null);
    setRoom(null);
    setSectorOptions([]);
    setRoomOptions([]);
    setContractItemsOptions([]);
    setSelectedContractItem(null);
    if (unit?.value) fetchSectors();
  }, [unit]);

  useEffect(() => {
    setRoom(null);
    setRoomOptions([]);
    setContractItemsOptions([]);
    setSelectedContractItem(null);
    if (sector?.value) fetchRooms('');
  }, [sector]);

  useEffect(() => {
    setContractItemsOptions([]);
    setSelectedContractItem(null);
    if (unit?.value) fetchContractItems('', 1);
  }, [unit, uf, city, sector, room]);

  const listEquipmentView = useFetch(
    `${Prefix.replace(/[\\"]/g, '')}/contract-item-views?per_page=${perPage}&page=${pagina + 1}&search=${search}&id_user=${idUser}&uf=${uf?.value || ''}&city=${city?.value || ''}&unit=${unit?.value || ''}&sector=${sector?.value || ''}&room=${room?.value || ''}`,
    [pagina, perPage, search, idUser, uf?.value, city?.value, unit?.value, sector?.value, room?.value]
  );

  const handleClose = () =>
    setEmployeeView({ ...employeeView, equipmentView: false, employees: true });

  const tableOptions = getTableOptions({
    pagina,
    perPage,
    listEquipmentView,
    HandleDelete: handleDelete,
    setPagina,
    setPerPage
  });

  const HandleSelect = (selected, nameField) => {
    setValueSelect(prev => {
      const filtered = prev.filter(p => p.nameField !== nameField);
      if (!selected) return filtered;
      return [...filtered, { nameField, value: selected.value, label: selected.label }];
    });
  };

  const HandleRegister = async () => {
    if (!idUser || !selectedContractItem) return;

    setIsSubmitting(true);

    const formData = {
      warning: valueSelect.find(v => v.nameField === 'warning')?.value ?? false,
      danger: valueSelect.find(v => v.nameField === 'danger')?.value ?? false,
      dark: valueSelect.find(v => v.nameField === 'dark')?.value ?? false
    };

    try {
      const contrai_item = selectedContractItem;

      const response = await api.post(
        Prefix.replace(/[\"]/g, '') + '/contract-item-views',
        {
          id_user: Number(idUser),
          id_contract_item: Number(contrai_item.value),
          warning: Boolean(formData.warning),
          danger: Boolean(formData.danger),
          dark: Boolean(formData.dark)
        }
      );

      await GlobalMutate(
        Prefix.replace(/[\\"]/g, '') +
        '/contract-item-views?per_page=' +
        perPage +
        '&page=' +
        (pagina + 1) +
        '&search=' +
        search +
        '&id_user=' +
        idUser
      );

      setIsSubmitting(false);

      Swal.fire({
        icon: 'success',
        title: 'Uow!',
        text: response.data.message,
        confirmButtonText: 'Fechar'
      });
    } catch (err) {
      setIsSubmitting(false);
      const msg = err?.response?.data?.message || 'Erro';
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: msg,
        confirmButtonText: 'Tentar novamente'
      });
    }
  };

  return (
    <>
      <Row>
        <Col md={4}>
          <label>Estado</label>
          <GlobalSelect
            value={uf}
            loadOptions={fetchUFs}
            isLoading={loadingUFs}
            onChange={setUf}
            placeholder="Selecione Estado..."
            styles={colourStyles}
            options={ufOptions}
          />
        </Col>

        <Col md={4}>
          <label>Cidade</label>
          <GlobalSelect
            value={city}
            loadOptions={fetchCities}
            isLoading={loadingCities}
            onChange={setCity}
            placeholder="Selecione Cidade..."
            styles={colourStyles}
            options={cityOptions}
            isDisabled={!uf?.value}
          />
        </Col>

        <Col md={4}>
          <label>Unidade</label>
          <GlobalSelect
            value={unit}
            loadOptions={fetchUnits}
            isLoading={loadingUnits}
            onChange={setUnit}
            placeholder="Selecione Unidade..."
            styles={colourStyles}
            options={unitOptions}
            isDisabled={!city?.value}
          />
        </Col>
      </Row>

      <Row style={{ marginTop: 10 }}>
        <Col md={4}>
          <label>Setor</label>
          <GlobalSelect
            value={sector}
            loadOptions={fetchSectors}
            isLoading={loadingSectors}
            onChange={setSector}
            placeholder="Selecione Setor..."
            styles={colourStyles}
            options={sectorOptions}
            isDisabled={!unit?.value}
          />
        </Col>

        <Col md={4}>
          <label>Sala</label>
          <GlobalSelect
            value={room}
            loadOptions={fetchRooms}
            isLoading={loadingRooms}
            onChange={setRoom}
            placeholder="Selecione Sala..."
            styles={colourStyles}
            options={roomOptions}
            isDisabled={!sector?.value}
          />
        </Col>

        <Col md={4}>
          <label>Item de Contrato</label>
          <GlobalSelect
            value={selectedContractItem}
            loadOptions={fetchContractItems}
            isLoading={loadingContractItems}
            onChange={setSelectedContractItem}
            placeholder="Selecione Item de Contrato..."
            styles={colourStyles}
            options={contractItemsOptions}
            isDisabled={!unit?.value}
          />
        </Col>
      </Row>


      <Row style={{ marginTop: 14 }}>
        <Col md={4}>
          <div className="formStep">
            <label className="important">Receber alerta médio?</label>
            <Select
              value={valueSelect
                .filter(fill => fill.nameField === 'warning')
                .map(map => ({ value: map.value, label: map.label }))}
              styles={colourStyles}
              onChange={e => HandleSelect(e, 'warning')}
              placeholder="Selecione..."
              options={alerts.map(a => ({ value: a.value, label: a.name }))}
            />
          </div>
        </Col>

        <Col md={4}>
          <div className="formStep">
            <label className="important">Receber alerta urgente?</label>
            <Select
              value={valueSelect
                .filter(fill => fill.nameField === 'danger')
                .map(map => ({ value: map.value, label: map.label }))}
              styles={colourStyles}
              onChange={e => HandleSelect(e, 'danger')}
              placeholder="Selecione..."
              options={alerts.map(a => ({ value: a.value, label: a.name }))}
            />
          </div>
        </Col>

        <Col md={4}>
          <div className="formStep">
            <label className="important">Receber alerta grave?</label>
            <Select
              value={valueSelect
                .filter(fill => fill.nameField === 'dark')
                .map(map => ({ value: map.value, label: map.label }))}
              styles={colourStyles}
              onChange={e => HandleSelect(e, 'dark')}
              placeholder="Selecione..."
              options={alerts.map(a => ({ value: a.value, label: a.name }))}
            />
          </div>
        </Col>
      </Row>

      <Row>
        <Col className="d-flex justify-content-center mt-4 mb-3">
          <Button
            type="button"
            style={{
              backgroundColor: '#1bc5bd',
              width: '160px',
              height: '60px',
              border: 0,
              borderRadius: '30px',
              marginTop: '20px'
            }}
            onClick={HandleRegister}
            disabled={isSubmitting}
          >
            Vincular
          </Button>
        </Col>
      </Row>

      <div style={{ position: 'relative', zIndex: 0, marginTop: 20 }}>
        <MUIDataTable
          data={listEquipmentView.data?.models?.data || []}
          columns={columns}
          options={tableOptions}
        />
      </div>

      <Row>
        <Col className="d-flex justify-content-center mt-4 mb-3">
          <Button
            type="button"
            style={{
              backgroundColor: '#1bc5bd',
              width: 160,
              height: 60,
              border: 0,
              borderRadius: 30
            }}
            onClick={handleClose}
          >
            Voltar
          </Button>
        </Col>
      </Row>
    </>

  );
};

export default EquipmentView;
